import { useTranslation } from 'react-i18next'
import { useEffect } from 'preact/hooks'
import {
  Divider,
  MainContentLayout,
  Alert,
  usePageLoader,
  Header,
  Content,
  Checkbox,
} from '@/components'
import { IdentityCard } from '@/components/IdentityCard'
import { DocumentTable } from '@/components/DocumentTable'
import {
  usePreventBackNavigation,
  useNavigationGuard,
} from '@/services/navigation-guard'
import { advanceToStep } from '@/services/navigation'
import { useAcceptAndSign } from '@/hooks/useAcceptAndSign'
import { useProcessData } from '@/hooks/useProcessData'
import { useProcessStatusHandler } from '@/hooks/useProcessStatusHandler'
import { useSummaryConsent } from '@/hooks/useSummaryConsent'
import { getConfig } from '../../../config'

function LegalNotice() {
  const { t } = useTranslation()
  const { termsOfServiceUrl } = getConfig()

  return (
    <span>
      {t('summary.checkbox.accept')}{' '}
      <a
        href={termsOfServiceUrl ?? '#'}
        target='_blank'
        className='text-cencert-blue underline hover:no-underline'
      >
        {t('summary.checkbox.termsLink')}
      </a>
      .
    </span>
  )
}

export function SummaryPage() {
  const { t } = useTranslation()

  usePreventBackNavigation()

  const { navigateTo } = useNavigationGuard()
  const { showPageLoader, hidePageLoader } = usePageLoader()

  const { handleProcessStatus } = useProcessStatusHandler()

  const { userData, documents, isLoading, error } =
    useProcessData(handleProcessStatus)

  const {
    isAccepted,
    consentError,
    setIsAccepted,
    validateConsent,
    clearConsentError,
  } = useSummaryConsent()

  const {
    acceptAndSign,
    error: signingError,
    isLoading: isAcceptingPending,
  } = useAcceptAndSign()

  useEffect(() => {
    if (isLoading || isAcceptingPending) {
      showPageLoader()
    } else {
      hidePageLoader()
    }
    return () => {
      hidePageLoader()
    }
  }, [isLoading, isAcceptingPending, showPageLoader, hidePageLoader])

  const handleContinue = async () => {
    if (!validateConsent()) {
      return
    }

    try {
      await acceptAndSign()
      if (advanceToStep('waiting-for-signature')) {
        hidePageLoader()
        navigateTo('/waiting-for-signature')
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error during navigation:', error)
    }
  }

  const combinedError = signingError ?? error

  if (combinedError) {
    return (
      <div className='bg-cencert-bg-gray'>
        <Header />
        <Content showSidebar={true}>
          <MainContentLayout
            title={t('summary.title')}
            currentStep={3}
            totalSteps={4}
            showCTA={false}
          >
            <Alert
              variant='error'
              message={combinedError || t('summary.document.fetchError')}
            />
          </MainContentLayout>
        </Content>
      </div>
    )
  }

  return (
    <div className='bg-cencert-bg-gray'>
      <Header />

      <Content showSidebar={true}>
        <MainContentLayout
          title={t('summary.title')}
          currentStep={3}
          totalSteps={4}
          ctaButtonText={t('summary.button.generateCertificate')}
          ctaButtonOnClick={handleContinue}
          ctaButtonDisabled={isAcceptingPending}
          showCTA={true}
        >
          <div className='my-8' />

          <Divider />
          <div className='bg-white rounded-large py-4'>
            <IdentityCard
              firstName={userData?.firstName ?? ''}
              lastName={userData?.surname ?? ''}
              pesel={userData?.dnSerialNumber ?? ''}
            />
          </div>
          <Divider />

          <DocumentTable
            documents={documents}
            readonly={true}
            className='my-8'
          />

          <Checkbox
            checked={isAccepted}
            className='my-8'
            onChange={e => {
              setIsAccepted((e.target as HTMLInputElement).checked)
              clearConsentError()
            }}
            label={<LegalNotice />}
            required
            error={!!consentError}
            {...(consentError && {
              errorMessage: consentError,
            })}
          />
        </MainContentLayout>
      </Content>
    </div>
  )
}
